import { MutableRefObject, useEffect, useRef } from "react";
import { mp4Src, webmSrc } from "../../../utils/noSleepFiles";
import { wait } from "../../../utils/wait";
import {
  EquipmentType,
  RecipeType,
  StepStage,
  StepZutat,
} from "@/types/baseTypes";
import { customSanitize } from "@/utils/customSanitize";
import { useGlobalContext } from "@aleksundshantu.com/aunds-utils-react/contexts";
import RecipeIngredientTable from "../../RecipeComponents/RecipeSingleView/RecipeIngredientList/RecipeIngredientTable/RecipeIngredientTable";
import { useGoogleCloudSpeechSynthesis } from "@/utils/useSpeechSynthesis";
import { Section } from "../../Global/Section/Section";
import {
  OptionsRecipeType,
  OptionsTopfiGraphicsType,
} from "@/types/OptionType";
import RecipeTopfi from "../../RecipeComponents/RecipeSingleView/RecipeTopfi/RecipeTopfi";
import styles from "../CookingMode.module.scss";
import { ToggleSwitch } from "../../Global/ToggleSwitch/ToggleSwitch";
import EquipmentRecipe from "../EquipmentRecipe/EquipmentRecipe";
import StepsContent from "./StepsContent/StepsContent";
import { useRouter } from "next/router";
import { Button } from "@/components/Global/Button/Button";

interface BodyRecipeProps {
  recipeDetails: RecipeType;
  servingsCount: number;
  setServingsCount: (n: number) => void;
  ingredients: StepZutat[];
  currentStep: number;
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  hasUserConsent: boolean;
  setUserConsent?: (b: boolean) => void;
  stepText: MutableRefObject<HTMLDivElement | null>;
  killSpeechRecognition: boolean;
  setKillSpeechRecognition: (b: boolean) => void;
  shouldMuteAudio: boolean;
  setShouldMuteAudio: (b: boolean) => void;
  cookingOptions?: OptionsRecipeType["cooking_mode"];
  topfiGraphics: OptionsTopfiGraphicsType;
  equipmentList?: EquipmentType[];
  headerHeight: number;
}

export const BodyRecipe = ({
  recipeDetails,
  ingredients,
  setCurrentStep,
  currentStep,
  stepText,
  shouldMuteAudio,
  setShouldMuteAudio,
  cookingOptions,
  topfiGraphics,
  equipmentList,
  headerHeight,
}: BodyRecipeProps) => {
  const { getLabel } = useGlobalContext();
  const googleCloudTTS = useGoogleCloudSpeechSynthesis();
  const router = useRouter();

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const wakeLock = useRef<WakeLockSentinel | null>(null);
  const loopCancelRef = useRef(false);
  const stepHeadingRef = useRef<HTMLHeadingElement | null>(null);

  /* --------------------------------- Steps stages helpers --------------------------------- */

  const STAGE_BY_INDEX: Record<number, StepStage> = {
    [-3]: "intro",
    [-2]: "ingredients",
    [-1]: "equipment",
  };

  const getStepStage = (currentStep: number, totalSteps: number): StepStage =>
    STAGE_BY_INDEX[currentStep] ??
    (currentStep >= totalSteps ? "outro" : "step");

  const COLUMNS_BY_STAGE: Record<StepStage, string> = {
    intro: "is-10-tablet is-8-desktop is-6-fullhd",
    ingredients: "is-10-tablet is-8-desktop is-6-fullhd",
    equipment: "is-12-tablet is-12-desktop is-10-fullhd",
    step: "is-12-tablet",
    outro: "is-10-tablet is-8-desktop is-6-fullhd",
  };

  const stage = getStepStage(
    currentStep,
    recipeDetails.steps_portion_big.length,
  );
  const columnClassName = `column ${COLUMNS_BY_STAGE[stage]}`;

  /* --------------------------------- TTS helpers --------------------------------- */

  const readRecipeWith =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (technology: any) =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (id: number, step: any): void => {
        technology.readText(id, step);
      };

  const readRecipeWithGoogle = readRecipeWith(googleCloudTTS);

  /* --------------------------------- Wake Lock helpers --------------------------------- */

  /** Check if the hidden video is currently playing. */
  const isVideoPlaying = (): boolean => {
    const v = videoRef.current;
    return !!(
      v &&
      v.currentTime > 0 &&
      !v.paused &&
      !v.ended &&
      v.readyState > 2
    );
  };

  /** Loop activation to keep the device awake via a small hidden video. */
  const activate = async (): Promise<void> => {
    if (loopCancelRef.current) return;

    const v = videoRef.current;
    if (!v) return;

    if (isVideoPlaying()) {
      v.pause();
    }

    v.currentTime = 0;

    try {
      await v.play();
    } catch (err) {
      console.warn("Video play was blocked by the browser.", err);
    }

    await wait(10000);
    if (!loopCancelRef.current) {
      void activate();
    }
  };

  /** Fallback strategy for wake lock using a looping video element. */
  const videoWakeLock = (): void => {
    const v = (videoRef.current = document.createElement("video"));
    v.setAttribute("playsinline", "");

    const s1 = document.createElement("source");
    s1.src = mp4Src; // assume string
    s1.type = "video/mp4";
    v.append(s1);

    const s2 = document.createElement("source");
    s2.src = webmSrc; // assume string
    s2.type = "video/webm";
    v.append(s2);

    // console.log("Running activate");
    void activate();
    // console.log("Activated video");
  };

  /* --------------------------------- Actions --------------------------------- */

  /** Read the current step using Google Cloud TTS (honors mute). */
  const readCurrentStep = (): void => {
    if (shouldMuteAudio) return;

    let stepParam: number | string;
    switch (currentStep) {
      case -2:
        stepParam = "ingredients";
        break;
      case -1:
        stepParam = "equipment";
        break;
      case recipeDetails.steps_portion_big.length:
        stepParam = "outro";
        break;
      default:
        stepParam = currentStep;
        break;
    }

    if (typeof stepParam === "string" || stepParam >= 0) {
      readRecipeWithGoogle(recipeDetails.rezept_id, stepParam);
    }
  };

  /** Initialize cooking mode: request wake lock (or fallback) and jump to ingredients. */
  const startCookingMode = (): void => {
    if (navigator.wakeLock) {
      navigator.wakeLock
        .request("screen")
        .then((wakeLockR) => {
          wakeLock.current = wakeLockR;
        })
        .catch(() => {
          videoWakeLock();
        });
    } else {
      videoWakeLock();
    }

    googleCloudTTS.stopReading();
    setCurrentStep(-2);
  };

  /* --------------------------------- Effects --------------------------------- */

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      loopCancelRef.current = true;

      const v = videoRef.current;
      if (v) {
        try {
          v.pause();
        } catch {
          /* ignore */
        }
        while (v.firstChild) v.removeChild(v.firstChild);
      }

      wakeLock.current?.release().catch(() => {
        /* ignore */
      });
    };
  }, []);

  // Read step when it changes.
  useEffect(() => {
    readCurrentStep();
    // eslint-disable-next-line
  }, [currentStep]);

  // Mute/unmute TTS when toggle changes.
  useEffect(() => {
    if (shouldMuteAudio) googleCloudTTS.muteReading();
    else googleCloudTTS.unmuteReading();
  }, [shouldMuteAudio, googleCloudTTS]);

  return (
    <Section
      className={`${styles["recipe-body"]}`}
      columnsClassName="is-multiline is-vcentered is-centered"
      style={{ minHeight: `calc(100vh - ${headerHeight}px)` }}
    >
      <div className={columnClassName}>
        <ol className={`${styles["steps-list"]}`} id="steps">
          {/* Intro = -3 (before ingredients) */}

          <li
            id="step-panel-intro"
            role="region"
            aria-labelledby="step-heading-intro"
            hidden={currentStep !== -3}
            className={styles.intro}
          >
            <h2
              id="step-heading-intro"
              tabIndex={-1}
              ref={currentStep === -3 ? stepHeadingRef : null}
              dangerouslySetInnerHTML={{
                __html: customSanitize(
                  getLabel(
                    "cooking_mode_step_by_step_title",
                    "Schritt-für-Schritt-Anzeige",
                  ),
                ),
              }}
            />
            {cookingOptions?.step_by_step_info &&
              cookingOptions?.step_by_step_info?.length > 0 && (
                <div className={styles["intro-info"]}>
                  {cookingOptions?.step_by_step_info?.map(
                    (
                      step: OptionsRecipeType["cooking_mode"]["step_by_step_info"][number],
                      index: number,
                    ) => (
                      <p key={index}>
                        {step.text +
                          `${index < cookingOptions?.step_by_step_info.length - 1 ? "," : ""}`}
                      </p>
                    ),
                  )}
                </div>
              )}
            <ToggleSwitch
              label={getLabel(
                "cooking_mode_read_aloud_label",
                "Rezepte vorlesen",
              )}
              setValue={setShouldMuteAudio}
              value={!shouldMuteAudio}
            />

            <RecipeTopfi
              topfiGraphics={topfiGraphics}
              name="Topfi-kochmodus-intro"
              activateButton
              onActivate={startCookingMode}
              recipeTitle={recipeDetails.rezept_titel}
            />
          </li>

          {/* Ingredients = -2 */}

          <li
            id="step-panel-ingredients"
            role="region"
            aria-labelledby="step-heading-ingredients"
            hidden={currentStep !== -2}
            // className={styles.ingredients}
          >
            <h2
              id="step-heading-ingredients"
              tabIndex={-1}
              ref={currentStep === -2 ? stepHeadingRef : null}
              className={styles.title}
            >
              {getLabel("ingredients_list", "Zutatenliste")}
            </h2>
            <RecipeIngredientTable ingredients={ingredients} hasBorder />
          </li>
          {/* Utensils = -1 */}
          <li
            id="step-panel-equipment"
            role="region"
            aria-labelledby="step-heading-equipment"
            hidden={currentStep !== -1}
          >
            <h2
              id="step-heading-equipment"
              tabIndex={-1}
              ref={currentStep === -1 ? stepHeadingRef : null}
              className={styles.title}
            >
              {getLabel("cooking_mode_utensils_title", "Das brauchst du:")}
            </h2>
            {equipmentList?.length ? (
              <EquipmentRecipe equipmentList={equipmentList} />
            ) : null}
          </li>

          {/* Step content = 0...N */}
          {recipeDetails.steps_portion_big.map((step, i) => (
            <li
              key={i}
              id={`step-panel-${i}`}
              role="region"
              aria-labelledby={`step-heading-${i}`}
              hidden={currentStep !== i}
            >
              {recipeDetails.schwierigkeitsgrad === 3 && (
                <div className="columns is-centered">
                  <div className="column is-8-tablet">
                    <h2
                      className="pb-0"
                      id={`step-heading-${i}`}
                      tabIndex={-1}
                      ref={currentStep === i ? stepHeadingRef : null}
                    >
                      {i + 1} . {getLabel("step", "Schritt")}
                      {step?.step_titel ? `: ${step.step_titel}` : ""}
                    </h2>
                  </div>
                </div>
              )}

              <StepsContent
                recipeDetails={recipeDetails}
                ingredients={ingredients}
                stepIndex={i}
                stepText={stepText}
              />
            </li>
          ))}

          {/*  Outro = N  / End button */}

          <li
            id="step-panel-outro"
            role="region"
            aria-labelledby="step-heading-outro"
            hidden={currentStep !== recipeDetails.steps_portion_big.length}
            className={styles.outro}
          >
            <RecipeTopfi
              topfiGraphics={topfiGraphics}
              name="Topfi-Kochmodus-Outro"
              recipeTitle={recipeDetails.rezept_titel}
            />
            <Button
              className="mx-auto mt-6"
              type="primary"
              href="#"
              target={""}
              onClick={() => {
                googleCloudTTS.stopReading();
                router.push(`/rezepte/${recipeDetails.rezept_id}`);
              }}
              aria-label={getLabel(
                "cooking_mode_end_button_label",
                "Kochmodus schliessen",
              )}
              dangerously
            >
              {getLabel(
                "cooking_mode_end_button_label",
                "Kochmodus schliessen",
              )}
            </Button>
          </li>
        </ol>
      </div>
    </Section>
  );
};

export default BodyRecipe;
